"use client"

import { useTranslations } from "next-intl"
import { useTheme } from "next-themes"
import { MoonIcon, SunIcon } from "lucide-react"

/**
 * Light/dark theme toggle for the store header. Complements the existing "d"
 * hotkey (`theme-provider.tsx`) with a visible control.
 *
 * Hydration-safe without a `mounted` gate: BOTH icons are always rendered and
 * CSS (`dark:` variants keyed off the `class` attribute next-themes stamps on
 * <html> before paint) decides which one shows — the server markup never
 * depends on the client-only theme value.
 *
 * Renders nothing when the tenant forces a single scheme (`theme.colorMode`
 * `light`/`dark` → next-themes `forcedTheme`, see `theme-provider.tsx`).
 * `forcedTheme` comes from props, not storage, so SSR and client agree.
 */
export function ThemeToggle() {
  const { resolvedTheme, setTheme, forcedTheme } = useTheme()
  const t = useTranslations("nav")

  if (forcedTheme) return null

  return (
    <button
      type="button"
      onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
      aria-label={t("toggleTheme")}
      title={t("toggleTheme")}
      className="inline-flex items-center justify-center rounded-4xl p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <SunIcon className="size-4 dark:hidden" aria-hidden />
      <MoonIcon className="hidden size-4 dark:block" aria-hidden />
    </button>
  )
}

/** False when the tenant pins one colour scheme, so the toggle is hidden. */
export function useCanToggleTheme(): boolean {
  return !useTheme().forcedTheme
}
